"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateVegaShock = exports.calculateVegaBucketTable = void 0;
const calculationsCore_1 = require("../calculationsCore");
const optionPricer_1 = require("../optionPricer");
const pnlCalculations_1 = require("../pnlCalculations");
const types_1 = require("../types");
const utils_1 = require("../utils");
const utils_2 = require("./utils");
const calculateVegaBucketTable = (positions, tickers, calculationTimestamp) => {
    const vegaBucketTable = {};
    const dvolPositions = positions.filter((p) => (0, calculationsCore_1.getInstrumentType)(p.instrument) === types_1.PositionType.VolatilityFuture);
    const notDvolPositions = positions.filter((p) => (0, calculationsCore_1.getInstrumentType)(p.instrument) !== types_1.PositionType.VolatilityFuture);
    const groupedPositionsByCurrencyExpiry = (0, utils_2.groupPositionsByCurrencyAndExpiry)(notDvolPositions);
    for (const currency of Object.keys(groupedPositionsByCurrencyExpiry)) {
        vegaBucketTable[currency] = {};
        for (const expiry of Object.keys(groupedPositionsByCurrencyExpiry[currency])) {
            // sums vegas
            const vegaSum = groupedPositionsByCurrencyExpiry[currency][expiry].reduce((memo, currentPosition) => memo +
                (0, optionPricer_1.getPositionVega)(currentPosition, tickers[currentPosition.instrument], calculationTimestamp), 0);
            vegaBucketTable[currency][expiry] = vegaSum;
        }
    }
    for (const position of dvolPositions) {
        const expiryTimestamp = (0, calculationsCore_1.getExpiryTimestampFromInstrumentString)(position.instrument);
        const trackingTimestamp = (0, calculationsCore_1.offsetTimestampByDays)(expiryTimestamp, 30);
        const expiryString = (0, calculationsCore_1.getDayExpiryRepresentationFromTimestamp)(trackingTimestamp);
        const underlyingCurrency = (0, pnlCalculations_1.getVolUnerlyingCurrencyFromInstrumentString)(position.instrument);
        if (!vegaBucketTable[underlyingCurrency]) {
            vegaBucketTable[underlyingCurrency] = {};
        }
        vegaBucketTable[underlyingCurrency][expiryString] = vegaBucketTable[underlyingCurrency][expiryString]
            ? Math.abs(vegaBucketTable[underlyingCurrency][expiryString] +
                position.amountCurrency)
            : Math.abs(position.amountCurrency);
    }
    /**
     * We cannot do abs on line 51 vegaBucketTable[currency][expiry] = vegaSum; because we need to expand it with dvol
     * futures and then do absolute. We have a case where this can still be negative.
     *
     * We could put the abs in furhter up in the chain when calculating but there would be a case where we show this so its better if we just
     * reiterate over and make sure that there are no negatives.
     */
    for (const currency of Object.keys(groupedPositionsByCurrencyExpiry)) {
        for (const expiry of Object.keys(groupedPositionsByCurrencyExpiry[currency])) {
            vegaBucketTable[currency][expiry] = Math.abs(vegaBucketTable[currency][expiry]);
        }
    }
    return vegaBucketTable;
};
exports.calculateVegaBucketTable = calculateVegaBucketTable;
const calculateVegaShock = (positions, tickers, pmParams, calculationTimestamp) => {
    const vegaBucketTable = (0, exports.calculateVegaBucketTable)(positions, tickers, calculationTimestamp);
    const vegaShockTable = {};
    const vegaSumPerCurrency = {};
    const allExpieries = new Set();
    const allCurrencies = Object.keys(pmParams.currencyParams).filter((c) => !c.includes("DVOL"));
    for (const currency of allCurrencies) {
        let sumCurrency = 0;
        vegaShockTable[currency] = {};
        if (vegaBucketTable[currency]) {
            for (const [expiry, vegaBucketForExpiry] of Object.entries(vegaBucketTable[currency])) {
                allExpieries.add(expiry);
                const absVega = Math.abs(vegaBucketForExpiry);
                const vegaShock = absVega < pmParams.general.vegaShock.vegaLevel
                    ? 0
                    : (absVega - pmParams.general.vegaShockLV) *
                        pmParams.general.vegaShock.multiplier;
                sumCurrency += vegaShock;
                vegaShockTable[currency][expiry] = vegaShock;
            }
        }
        vegaSumPerCurrency[currency] = sumCurrency;
    }
    const totalVega = Object.keys(vegaSumPerCurrency).reduce((memo, currentCurrency) => {
        return memo + vegaSumPerCurrency[currentCurrency];
    }, 0);
    return {
        totalVega,
        vegaSumPerCurrency,
        vegaBucketTable,
        vegaShockTable,
        allExpieries: Array.from(allExpieries).sort(utils_1.sortExpiryStringsPredicate),
        allCurrencies,
    };
};
exports.calculateVegaShock = calculateVegaShock;
